<template>
  <div class="datetimepicker" :class="wrapperClass">
    <input
      :type="inline ? 'hidden' : 'text'"
      :class="inputClass"
      :name="name"
      :id="id"
      @click="showCalendar()"
      :value="formattedValue"
      ref="input"
      :placeholder="placeholder"
      :readonly="readonly"
      v-on:input="checkValue($event.target.value)"
      autocomplete="off"
    />

    <!-- Day View -->
    <div class="calendar" v-show="showDayView" v-bind:style="calendarStyle">
      <header>
        <span
          @click="previousMonth"
          class="prev"
          v-bind:class="{ disabled: previousMonthDisabled(currDate) }"
          >&lt;</span
        >
        <span @click="showMonthCalendar" class="up"
          >{{ currMonthName }} {{ currYear }}</span
        >
        <span
          @click="nextMonth"
          class="next"
          v-bind:class="{ disabled: nextMonthDisabled(currDate) }"
          >&gt;</span
        >
      </header>
      <span
        class="cell day-header"
        v-bind:key="`days-of-week-${index}`"
        v-for="(d, index) in daysOfWeek"
        >{{ d }}</span
      >
      <span
        class="cell day blank"
        v-bind:key="`blank-days-${index}`"
        v-for="(d, index) in blankDays"
      ></span
      ><!--
            --><span
        class="cell day"
        v-bind:key="`days-${index}`"
        v-for="(day, index) in days"
        track-by="timestamp"
        v-bind:class="{
          selected: day.isSelected,
          disabled: day.isDisabled,
          highlighted: day.isHighlighted
        }"
        @click="selectDate(day)"
        >{{ day.date }}</span
      >
      <footer>
        <div>
          Hour
          <select
            name="hour"
            :value="getHour"
            v-on:input="updateHour($event.target.value)"
          >
            <option
              v-bind:key="`hours-${index}`"
              v-for="(hour, index) in getHours"
              :value="hour"
              >{{ hour }}</option
            >
          </select>
        </div>
        <div>
          Minute
          <select
            name="minute"
            :value="getMinute"
            v-on:input="updateMinute($event.target.value)"
          >
            <option
              v-bind:key="`minutes-${index}`"
              v-for="(minute, index) in getMinutes"
              :value="minute"
              >{{ minute }}</option
            >
          </select>
        </div>
      </footer>
    </div>

    <!-- Month View -->
    <div
      class="calendar"
      v-show="showMonthView"
      v-bind:style="calendarStyleSecondary"
    >
      <header>
        <span
          @click="previousYear"
          class="prev"
          v-bind:class="{ disabled: previousYearDisabled(currDate) }"
          >&lt;</span
        >
        <span @click="showYearCalendar" class="up">{{ getYear() }}</span>
        <span
          @click="nextYear"
          class="next"
          v-bind:class="{ disabled: nextYearDisabled(currDate) }"
          >&gt;</span
        >
      </header>
      <span
        class="cell month"
        v-bind:key="`months-${index}`"
        v-for="(month, index) in months"
        track-by="timestamp"
        v-bind:class="{
          selected: month.isSelected,
          disabled: month.isDisabled
        }"
        @click.stop="selectMonth(month)"
        >{{ month.month }}</span
      >
    </div>

    <!-- Year View -->
    <div
      class="calendar"
      v-show="showYearView"
      v-bind:style="calendarStyleSecondary"
    >
      <header>
        <span
          @click="previousDecade"
          class="prev"
          v-bind:class="{ disabled: previousDecadeDisabled(currDate) }"
          >&lt;</span
        >
        <span>{{ getDecade() }}</span>
        <span
          @click="nextDecade"
          class="next"
          v-bind:class="{ disabled: nextMonthDisabled(currDate) }"
          >&gt;</span
        >
      </header>
      <span
        class="cell year"
        v-bind:key="`years-${index}`"
        v-for="(year, index) in years"
        track-by="timestamp"
        v-bind:class="{ selected: year.isSelected, disabled: year.isDisabled }"
        @click.stop="selectYear(year)"
        >{{ year.year }}</span
      >
    </div>
  </div>
</template>

<script>
import DateUtils from '../../utils/DateUtils.js';
import DateLanguages from '../../utils/DateLanguages.js';

export default {
  props: {
    value: {
      validator: function(val) {
        return val === null || val instanceof Date || typeof val === 'string';
      }
    },
    name: {
      value: String
    },
    id: {
      value: String
    },
    format: {
      value: String,
      default: 'dd MMM yyyy'
    },
    language: {
      value: String,
      default: 'en'
    },
    disabled: {
      type: Object
    },
    highlighted: {
      type: Object
    },
    placeholder: {
      type: String
    },
    inline: {
      type: Boolean
    },
    inputClass: {
      type: String
    },
    wrapperClass: {
      type: String
    },
    mondayFirst: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      /*
       * Vue cannot observe changes to a Date Object so date must be stored as a timestamp
       * This represents the first day of the current viewing month
       * {Number}
       */
      currDate: new Date(
        new Date().getFullYear(),
        new Date().getMonth(),
        new Date().getDate(),
        new Date().getHours(),
        new Date().getMinutes()
      ).getTime(),
      /*
       * Selected Date
       * {Date}
       */
      selectedDate: null,
      /*
       * Flags to show calendar views
       * {Boolean}
       */
      showDayView: false,
      showMonthView: false,
      showYearView: false,
      /*
       * Helper arrays for names
       */
      /*
       * Positioning
       */
      calendarHeight: 0
    };
  },

  computed: {
    getHours() {
      let hours = [];
      var i = 0;
      for (i = 0; i < 24; i++) {
        i < 10 && i >= 0 ? (i = '0' + i) : (i = i.toString());

        hours.push(i);
      }
      return hours;
    },

    getMinutes() {
      let minutes = [];
      var i = 0;
      for (i = 0; i < 60; i++) {
        i < 10 && i >= 0 ? (i = '0' + i) : (i = i.toString());

        minutes.push(i);
      }
      return minutes;
    },

    getHour() {
      let dateObj;
      let hour;
      this.selectedDate
        ? (dateObj = this.selectedDate)
        : (dateObj = new Date(this.currDate));

      dateObj.getHours() < 10 && dateObj.getHours() >= 0
        ? (hour = '0' + dateObj.getHours())
        : (hour = dateObj.getHours());

      return hour;
    },

    getMinute() {
      let dateObj;
      let minute;
      this.selectedDate
        ? (dateObj = this.selectedDate)
        : (dateObj = new Date(this.currDate));

      dateObj.getMinutes() < 10 && dateObj.getMinutes() >= 0
        ? (minute = '0' + dateObj.getMinutes())
        : (minute = dateObj.getMinutes());

      return minute;
    },

    formattedValue() {
      if (this.selectedDate && DateUtils.isValidDate(this.selectedDate)) {
        return DateUtils.formatDate(
          new Date(this.selectedDate),
          'dd/MM/yyyy HH:mm',
          this.translation
        );
      }

      return null;
    },
    translation() {
      return DateLanguages.translations[this.language];
    },
    currMonthName() {
      const d = new Date(this.currDate);
      return DateUtils.getMonthNameAbbr(
        d.getMonth(),
        this.translation.months.abbr
      );
    },
    currYear() {
      const d = new Date(this.currDate);
      return d.getFullYear();
    },
    /**
     * Returns the day number of the week less one for the first of the current month
     * Used to show amount of empty cells before the first in the day calendar layout
     * @return {Number}
     */
    blankDays() {
      const d = new Date(this.currDate);
      let dObj = new Date(
        d.getFullYear(),
        d.getMonth(),
        1,
        d.getHours(),
        d.getMinutes()
      );
      if (this.mondayFirst) {
        return dObj.getDay() > 0 ? dObj.getDay() - 1 : 6;
      }
      return dObj.getDay();
    },
    daysOfWeek() {
      if (this.mondayFirst) {
        const tempDays = this.translation.days.slice();
        tempDays.push(tempDays.shift());
        return tempDays;
      }
      return this.translation.days;
    },
    days() {
      const d = new Date(this.currDate);
      let days = [];
      // set up a new date object to the beginning of the current 'page'
      let dObj = new Date(
        d.getFullYear(),
        d.getMonth(),
        1,
        d.getHours(),
        d.getMinutes()
      );
      let daysInMonth = DateUtils.daysInMonth(
        dObj.getFullYear(),
        dObj.getMonth()
      );
      for (let i = 0; i < daysInMonth; i++) {
        days.push({
          date: dObj.getDate(),
          timestamp: dObj.getTime(),
          isSelected: this.isSelectedDate(dObj),
          isDisabled: this.isDisabledDate(dObj),
          isHighlighted: this.isHighlightedDate(dObj)
        });
        dObj.setDate(dObj.getDate() + 1);
      }
      return days;
    },
    months() {
      const d = new Date(this.currDate);
      let months = [];
      // set up a new date object to the beginning of the current 'page'
      let dObj = new Date(
        d.getFullYear(),
        0,
        d.getDate(),
        d.getHours(),
        d.getMinutes()
      );
      for (let i = 0; i < 12; i++) {
        months.push({
          month: DateUtils.getMonthName(i, this.translation.months.original),
          timestamp: dObj.getTime(),
          isSelected: this.isSelectedMonth(dObj),
          isDisabled: this.isDisabledMonth(dObj)
        });
        dObj.setMonth(dObj.getMonth() + 1);
      }
      return months;
    },
    years() {
      const d = new Date(this.currDate);
      let years = [];
      // set up a new date object to the beginning of the current 'page'
      let dObj = new Date(
        Math.floor(d.getFullYear() / 10) * 10,
        d.getMonth(),
        d.getDate(),
        d.getHours(),
        d.getMinutes()
      );
      for (let i = 0; i < 10; i++) {
        years.push({
          year: dObj.getFullYear(),
          timestamp: dObj.getTime(),
          isSelected: this.isSelectedYear(dObj),
          isDisabled: this.isDisabledYear(dObj)
        });
        dObj.setFullYear(dObj.getFullYear() + 1);
      }
      return years;
    },
    calendarStyle() {
      let elSize = {
        top: 0,
        height: 0
      };
      if (this.$el) {
        elSize = this.$el.getBoundingClientRect();
      }
      let heightNeeded = elSize.top + elSize.height + this.calendarHeight || 0;
      let styles = {};
      // if the calendar doesn't fit on the window without scrolling position it above the input
      if (heightNeeded > window.innerHeight) {
        styles = {
          bottom: elSize.height + 'px'
        };
      }
      if (this.isInline()) {
        styles.position = 'static';
      }

      return styles;
    },
    calendarStyleSecondary() {
      return this.isInline() ? { position: 'static' } : {};
    }
  },
  methods: {
    updateHour(hour) {
      if (!this.selectedDate) {
        let date = new Date();
        this.setDate(
          new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            hour,
            date.getMinutes()
          ).getTime()
        );
      } else {
        let date = new Date(this.selectedDate);
        date.setHours(hour);
        this.setDate(date.getTime());
      }
    },

    updateMinute(minute) {
      if (!this.selectedDate) {
        let date = new Date();
        this.setDate(
          new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            date.getHours(),
            minute
          ).getTime()
        );
      } else {
        let date = new Date(this.selectedDate);
        date.setMinutes(minute);
        this.setDate(date.getTime());
      }
    },

    close() {
      this.showDayView = this.showMonthView = this.showYearView = false;
    },
    isOpen() {
      return this.showDayView || this.showMonthView || this.showYearView;
    },
    isInline() {
      return typeof this.inline !== 'undefined' && this.inline;
    },
    showCalendar() {
      this.$refs.input.focus();
      if (this.readonly) {
        return false;
      }
      if (this.isInline()) {
        return false;
      }
      if (this.isOpen()) {
        return this.close();
      }
      this.showDayCalendar();
    },
    showDayCalendar() {
      this.close();
      this.showDayView = true;
      this.$emit('opened');
    },
    showMonthCalendar() {
      this.close();
      this.showMonthView = true;
    },
    showYearCalendar() {
      this.close();
      this.showYearView = true;
    },

    setDate(timestamp) {
      this.selectedDate = new Date(timestamp);
      this.currDate = new Date(
        this.selectedDate.getFullYear(),
        this.selectedDate.getMonth(),
        this.selectedDate.getDay(),
        this.selectedDate.getHours(),
        this.selectedDate.getMinutes()
      ).getTime();
      this.$emit('selected', new Date(timestamp));
      this.$emit('input', new Date(timestamp));
    },

    /**
     * @param {Object} day
     */
    selectDate(day) {
      if (day.isDisabled) {
        return false;
      }
      this.setDate(day.timestamp);
      if (this.isInline()) {
        return this.showDayCalendar();
      }
      this.close();
    },

    /**
     * @param {Object} month
     */
    selectMonth(month) {
      if (month.isDisabled) {
        return false;
      }
      this.currDate = month.timestamp;
      this.showDayCalendar();
    },

    /**
     * @param {Object} year
     */
    selectYear(year) {
      if (year.isDisabled) {
        return false;
      }
      this.currDate = year.timestamp;
      this.showMonthCalendar();
    },

    /**
     * @return {Number}
     */
    getMonth() {
      let d = new Date(this.currDate);
      return d.getMonth();
    },

    /**
     * @return {Number}
     */
    getYear() {
      let d = new Date(this.currDate);
      return d.getFullYear();
    },

    /**
     * @return {String}
     */
    getDecade() {
      let d = new Date(this.currDate);
      let sD = Math.floor(d.getFullYear() / 10) * 10;
      return sD + "'s";
    },

    previousMonth() {
      if (this.previousMonthDisabled()) {
        return false;
      }
      let d = new Date(this.currDate);
      d.setMonth(d.getMonth() - 1);
      this.currDate = d.getTime();
    },

    previousMonthDisabled() {
      if (
        typeof this.disabled === 'undefined' ||
        typeof this.disabled.to === 'undefined' ||
        !this.disabled.to
      ) {
        return false;
      }
      let d = new Date(this.currDate);
      if (
        this.disabled.to.getMonth() >= d.getMonth() &&
        this.disabled.to.getFullYear() >= d.getFullYear()
      ) {
        return true;
      }
      return false;
    },

    nextMonth() {
      if (this.nextMonthDisabled()) {
        return false;
      }
      let d = new Date(this.currDate);
      const daysInMonth = DateUtils.daysInMonth(d.getFullYear(), d.getMonth());
      d.setDate(d.getDate() + daysInMonth);
      this.currDate = d.getTime();
    },

    nextMonthDisabled() {
      if (
        typeof this.disabled === 'undefined' ||
        typeof this.disabled.from === 'undefined' ||
        !this.disabled.from
      ) {
        return false;
      }
      let d = new Date(this.currDate);
      if (
        this.disabled.from.getMonth() <= d.getMonth() &&
        this.disabled.from.getFullYear() <= d.getFullYear()
      ) {
        return true;
      }
      return false;
    },

    previousYear() {
      if (this.previousYearDisabled()) {
        return false;
      }
      let d = new Date(this.currDate);
      d.setYear(d.getFullYear() - 1);
      this.currDate = d.getTime();
    },

    previousYearDisabled() {
      if (
        typeof this.disabled === 'undefined' ||
        typeof this.disabled.to === 'undefined' ||
        !this.disabled.to
      ) {
        return false;
      }
      let d = new Date(this.currDate);
      if (this.disabled.to.getFullYear() >= d.getFullYear()) {
        return true;
      }
      return false;
    },

    nextYear() {
      if (this.nextYearDisabled()) {
        return false;
      }
      let d = new Date(this.currDate);
      d.setYear(d.getFullYear() + 1);
      this.currDate = d.getTime();
    },

    nextYearDisabled() {
      if (
        typeof this.disabled === 'undefined' ||
        typeof this.disabled.from === 'undefined' ||
        !this.disabled.from
      ) {
        return false;
      }
      let d = new Date(this.currDate);
      if (this.disabled.from.getFullYear() <= d.getFullYear()) {
        return true;
      }
      return false;
    },

    previousDecade() {
      if (this.previousDecadeDisabled()) {
        return false;
      }
      let d = new Date(this.currDate);
      d.setYear(d.getFullYear() - 10);
      this.currDate = d.getTime();
    },

    previousDecadeDisabled() {
      if (
        typeof this.disabled === 'undefined' ||
        typeof this.disabled.to === 'undefined' ||
        !this.disabled.to
      ) {
        return false;
      }
      let d = new Date(this.currDate);
      if (
        Math.floor(this.disabled.to.getFullYear() / 10) * 10 >=
        Math.floor(d.getFullYear() / 10) * 10
      ) {
        return true;
      }
      return false;
    },

    nextDecade() {
      if (this.nextDecadeDisabled()) {
        return false;
      }
      let d = new Date(this.currDate);
      d.setYear(d.getFullYear() + 10);
      this.currDate = d.getTime();
    },

    nextDecadeDisabled() {
      if (
        typeof this.disabled === 'undefined' ||
        typeof this.disabled.from === 'undefined' ||
        !this.disabled.from
      ) {
        return false;
      }
      let d = new Date(this.currDate);
      if (
        Math.ceil(this.disabled.from.getFullYear() / 10) * 10 <=
        Math.ceil(d.getFullYear() / 10) * 10
      ) {
        return true;
      }
      return false;
    },

    /**
     * Whether a day is selected
     * @param {Date}
     * @return {Boolean}
     */
    isSelectedDate(dObj) {
      return (
        this.selectedDate &&
        this.selectedDate.toDateString() === dObj.toDateString()
      );
    },

    /**
     * Whether a day is disabled
     * @param {Date}
     * @return {Boolean}
     */
    isDisabledDate(date) {
      let disabled = false;

      if (typeof this.disabled === 'undefined') {
        return false;
      }

      if (typeof this.disabled.dates !== 'undefined') {
        this.disabled.dates.forEach(d => {
          if (date.toDateString() === d.toDateString()) {
            disabled = true;
            return true;
          }
        });
      }
      if (
        typeof this.disabled.to !== 'undefined' &&
        this.disabled.to &&
        date < this.disabled.to
      ) {
        disabled = true;
      }
      if (
        typeof this.disabled.from !== 'undefined' &&
        this.disabled.from &&
        date > this.disabled.from
      ) {
        disabled = true;
      }
      if (
        typeof this.disabled.days !== 'undefined' &&
        this.disabled.days.indexOf(date.getDay()) !== -1
      ) {
        disabled = true;
      }
      return disabled;
    },

    /**
     * Whether a day is highlighted (only if it is not disabled already)
     * @param {Date}
     * @return {Boolean}
     */
    isHighlightedDate(date) {
      if (this.isDisabledDate(date)) {
        return false;
      }

      let highlighted = false;

      if (typeof this.highlighted === 'undefined') {
        return false;
      }

      if (typeof this.highlighted.dates !== 'undefined') {
        this.highlighted.dates.forEach(d => {
          if (date.toDateString() === d.toDateString()) {
            highlighted = true;
            return true;
          }
        });
      }

      if (
        this.isDefined(this.highlighted.from) &&
        this.isDefined(this.highlighted.to)
      ) {
        highlighted =
          date >= this.highlighted.from && date <= this.highlighted.to;
      }

      if (
        typeof this.highlighted.days !== 'undefined' &&
        this.highlighted.days.indexOf(date.getDay()) !== -1
      ) {
        highlighted = true;
      }
      return highlighted;
    },

    /**
     * Helper
     * @param  {mixed}  prop
     * @return {Boolean}
     */
    isDefined(prop) {
      return typeof prop !== 'undefined' && prop;
    },

    /**
     * Whether the selected date is in this month
     * @param {Date}
     * @return {Boolean}
     */
    isSelectedMonth(date) {
      return (
        this.selectedDate &&
        this.selectedDate.getFullYear() === date.getFullYear() &&
        this.selectedDate.getMonth() === date.getMonth()
      );
    },

    /**
     * Whether a month is disabled
     * @param {Date}
     * @return {Boolean}
     */
    isDisabledMonth(date) {
      let disabled = false;

      if (typeof this.disabled === 'undefined') {
        return false;
      }

      if (typeof this.disabled.to !== 'undefined' && this.disabled.to) {
        if (
          (date.getMonth() < this.disabled.to.getMonth() &&
            date.getFullYear() <= this.disabled.to.getFullYear()) ||
          date.getFullYear() < this.disabled.to.getFullYear()
        ) {
          disabled = true;
        }
      }
      if (typeof this.disabled.from !== 'undefined' && this.disabled.from) {
        if (
          (this.disabled.from &&
            date.getMonth() > this.disabled.from.getMonth() &&
            date.getFullYear() >= this.disabled.from.getFullYear()) ||
          date.getFullYear() > this.disabled.from.getFullYear()
        ) {
          disabled = true;
        }
      }
      return disabled;
    },

    /**
     * Whether a year is disabled
     * @param {Date}
     * @return {Boolean}
     */
    isSelectedYear(date) {
      return (
        this.selectedDate &&
        this.selectedDate.getFullYear() === date.getFullYear()
      );
    },

    /**
     * Whether a month is disabled
     * @param {Date}
     * @return {Boolean}
     */
    isDisabledYear(date) {
      let disabled = false;
      if (typeof this.disabled === 'undefined' || !this.disabled) {
        return false;
      }

      if (typeof this.disabled.to !== 'undefined' && this.disabled.to) {
        if (date.getFullYear() < this.disabled.to.getFullYear()) {
          disabled = true;
        }
      }
      if (typeof this.disabled.from !== 'undefined' && this.disabled.from) {
        if (date.getFullYear() > this.disabled.from.getFullYear()) {
          disabled = true;
        }
      }

      return disabled;
    },

    checkValue(date) {
      if (typeof date === 'string') {
        let datepattern = /^\d{2}\/\d{2}\/\d{4}\s+\d{2}:\d{2}$/;
        if (!datepattern.test(date)) {
          return false;
        }
        let repl = /^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2})$/;
        date = date.replace(repl, '$2/$1/$3 $4:$5');

        date = window.DateManager.parse(date);
      }
      if (window.DateManager.isValid(date)) {
        this.setValue(date);
        this.showDayCalendar();
      }
    },

    /**
     * Set the datepicker value
     * @param {Date|String|null} date
     */
    setValue(date) {
      if (typeof date === 'string') {
        let parsed = window.DateManager.parse(date);

        if (window.DateManager.isValid(parsed)) {
          date = isNaN(parsed.valueOf()) ? null : parsed;
          if (this.isDisabledDate(date)) {
            this.selectedDate = null;
            return;
          }
          this.selectedDate = date;
          this.currDate = new Date(
            date.getFullYear(),
            date.getMonth(),
            1
          ).getTime();
          this.$emit('selected', date);
        }
      }

      if (!date) {
        const d = new Date();
        this.currDate = new Date(d.getFullYear(), d.getMonth(), 1).getTime();
        this.selectedDate = null;
        return;
      }
      this.currDate = date;
      this.selectedDate = date;
      this.$emit('selected', date);
      this.$emit('updated', date);
      return;
    },

    init() {
      if (this.value) {
        this.setValue(this.value);
      }
      if (this.isInline()) {
        this.showDayCalendar();
      }

      this.$nextTick(() => {
        this.calendarHeight = this.$el
          .querySelector('.calendar')
          .getBoundingClientRect().height;
      });

      document.addEventListener(
        'click',
        e => {
          if (this.$el && !this.$el.contains(e.target)) {
            if (this.isInline()) {
              return this.showDayCalendar();
            }
            this.close();
          }
        },
        false
      );
    }
  },

  /**
   * Vue 2.x
   */
  mounted() {
    this.init();
  },

  watch: {
    value(value) {
      this.setValue(value);
    }
  }
};
</script>

<style scoped>
* {
  box-sizing: border-box;
}
.datetimepicker {
  position: relative;
}
.calendar {
  position: absolute;
  top: initial;
  bottom: 35px;
  z-index: 100;
  background: #fff;
  width: 300px;
  border: 1px solid #ccc;
}
.calendar header {
  display: block;
  line-height: 40px;
}
.calendar header span {
  display: inline-block;
  text-align: center;
  width: 71.42857142857143%;
  float: left;
}
.calendar header .next,
.calendar header .prev {
  width: 14.285714285714286%;
  float: left;
  text-indent: -10000px;
  position: relative;
}
.calendar header .next:after,
.calendar header .prev:after {
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  -webkit-transform: translateX(-50%) translateY(-50%);
  transform: translateX(-50%) translateY(-50%);
  border: 6px solid transparent;
}
.calendar header .prev:after {
  border-right: 10px solid #000;
  margin-left: -5px;
}
.calendar header .prev.disabled:after {
  border-right: 10px solid #ddd;
}
.calendar header .next:after {
  border-left: 10px solid #000;
  margin-left: 5px;
}
.calendar header .next.disabled:after {
  border-left: 10px solid #ddd;
}
.calendar header .next:not(.disabled),
.calendar header .prev:not(.disabled),
.calendar header .up:not(.disabled) {
  cursor: pointer;
}
.calendar header .next:not(.disabled):hover,
.calendar header .prev:not(.disabled):hover,
.calendar header .up:not(.disabled):hover {
  background: #eee;
}
.calendar .disabled {
  color: #ddd;
  cursor: default;
}
.calendar .cell {
  display: inline-block;
  padding: 0 5px;
  width: 14.285714285714286%;
  height: 40px;
  line-height: 40px;
  text-align: center;
  vertical-align: middle;
  border: 1px solid transparent;
}
.calendar .cell:not(.blank):not(.disabled).day,
.calendar .cell:not(.blank):not(.disabled).month,
.calendar .cell:not(.blank):not(.disabled).year {
  cursor: pointer;
}
.calendar .cell:not(.blank):not(.disabled).day:hover,
.calendar .cell:not(.blank):not(.disabled).month:hover,
.calendar .cell:not(.blank):not(.disabled).year:hover {
  border: 1px solid #4bd;
}
.calendar .cell.selected,
.calendar .cell.selected.highlighted,
.calendar .cell.selected:hover {
  background: #4bd;
}
.calendar .cell.highlighted {
  background: #cae5ed;
}
.calendar .cell.grey {
  color: #888;
}
.calendar .cell.grey:hover {
  background: inherit;
}
.calendar .cell.day-header {
  font-size: 75%;
  white-space: no-wrap;
  cursor: inherit;
}
.calendar .cell.day-header:hover {
  background: inherit;
}
.calendar .month,
.calendar .year {
  width: 33.333%;
}
.calendar footer {
  display: flex;
  padding: 15px;
  align-items: center;
  justify-content: space-around;
}
</style>
